import { Controller } from "@hotwired/stimulus"

// Refreshes the configurable timesheet form's primary time inputs
// (time_from / time_to / time_break) whenever the user changes the
// reported_date OR the selected employee, so the resolved work-time model
// (employee override > template > company) follows both the date and the
// employee instead of staying on the values the form was rendered with.
export default class extends Controller {
  static targets = ["dateInput"]
  static values = {
    url: String,
    templateId: Number,
  }

  connect() {
    // Bumped on every refresh and on supersession; an answer is applied only while its
    // request is still the latest, so a slow response cannot overwrite newer times.
    this.requestId = 0
    this.boundRefresh = () => this.refresh()
    this.boundSupersede = () => this.requestId++
    this.boundResume = (event) => this.resume(event.detail?.keep || [])
    const select = this.employeeSelect()
    if (select) select.addEventListener("change", this.boundRefresh)
    this.form?.addEventListener("default-times:superseded", this.boundSupersede)
    this.form?.addEventListener("default-times:resumed", this.boundResume)
  }

  disconnect() {
    const select = this.employeeSelect()
    if (select && this.boundRefresh) select.removeEventListener("change", this.boundRefresh)
    this.form?.removeEventListener("default-times:superseded", this.boundSupersede)
    this.form?.removeEventListener("default-times:resumed", this.boundResume)
  }

  // fill-from-source is done writing a report's times. A refresh it held off still runs,
  // but leaves the fields the report filled (`keep`) alone.
  resume(keep) {
    if (!this.refreshDeferred) return

    this.refreshDeferred = false
    this.refresh({ keep })
  }

  // `keep` lists the scalar time fields to leave untouched. The break-period rows are
  // rebuilt regardless: with them visible, time_break is their sum on save, so stale
  // rows would decide the Pause.
  async refresh({ keep = [] } = {}) {
    if (!this.hasDateInputTarget || !this.urlValue || !this.templateIdValue) return
    // Set by fill-from-source while it writes a report's own times into the form.
    if (this.form?.dataset.defaultTimesSuspended) {
      this.refreshDeferred = true
      return
    }

    const requestId = ++this.requestId

    const date = this.dateInputTarget.value
    if (!date) return

    const params = new URLSearchParams()
    params.append("template_id", this.templateIdValue)
    params.append("date", date)

    // Only resolve per-employee when exactly one is selected — with several
    // selected employees a single time set can't reflect all of their models.
    const employeeId = this.selectedEmployeeId()
    if (employeeId) params.append("employee_id", employeeId)

    try {
      const response = await fetch(`${this.urlValue}?${params.toString()}`, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      })

      if (!response.ok) return

      const data = await response.json()
      if (requestId !== this.requestId) return

      for (const field of ["time_from", "time_to", "time_break"]) {
        if (!keep.includes(field)) this.setTimeField(field, data[field])
      }
      this.rebuildBreakPeriods(data.break_periods || [])
    } catch (error) {
      console.error("Error fetching seasonal default times:", error)
    }
  }

  // Rebuild the von/bis break rows to match the newly selected date's seasonal
  // defaults — mirrors how the scalar fields refresh. No-op when the template
  // does not use break periods (no such container in the form).
  //
  // Where the template fixes the model's windows but lets the employee add more,
  // the form carries a second, read-only row template: only the fixed rows are
  // replaced then, and the employee's own pauses stay as they were entered.
  rebuildBreakPeriods(breakPeriods) {
    const container = this.breakPeriodsContainer()
    if (!container) return

    const predefinedTemplate = container.querySelector(
      "template[data-break-periods-predefined-template]",
    )
    const template =
      predefinedTemplate || container.querySelector('template[data-nested-form-target="template"]')
    const links = container.querySelector('[data-nested-form-target="links"]')
    if (!template || !links) return

    const replaced = predefinedTemplate
      ? "[data-break-period-row][data-break-period-predefined]"
      : "[data-break-period-row]"
    container.querySelectorAll(replaced).forEach((row) => row.remove())

    // Fixed rows go above the employee's own, where the server-rendered form puts them.
    const anchor =
      (predefinedTemplate && container.querySelector("[data-break-period-row]")) || links
    const base = new Date().getTime()
    breakPeriods.forEach((bp, index) => {
      const html = template.innerHTML.replaceAll("NEW_RECORD", `${base + index}`)
      anchor.insertAdjacentHTML("beforebegin", html)
      const row = anchor.previousElementSibling
      const from = row.querySelector("[data-break-from]")
      const to = row.querySelector("[data-break-to]")
      // These are the time picker's hidden fields; it syncs its visible half off `change`,
      // so assigning without dispatching would leave the row looking empty.
      this._setTime(from, bp.from)
      this._setTime(to, bp.to)
    })

    // Let break-periods-total recompute the read-only Pause from the new rows.
    container.dispatchEvent(new Event("input", { bubbles: true }))
  }

  _setTime(input, value) {
    if (!input) return

    input.value = value || ""
    // A row inserted a moment ago has no connected time picker yet to hear the change
    // below, so its visible half is written here directly.
    const display = input
      .closest('[data-controller~="time-picker"]')
      ?.querySelector('[data-time-picker-target="display"]')
    if (display) display.value = input.value
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // The nested-form container holding the break-period rows (distinguished from
  // the driving/repair/workshop nested forms by its break-specific markup).
  breakPeriodsContainer() {
    const form = this.element.closest("form") || this.element.querySelector("form")
    if (!form) return null

    return Array.from(form.querySelectorAll('[data-controller~="nested-form"]')).find((c) => {
      const tpl = c.querySelector('template[data-nested-form-target="template"]')
      return tpl && tpl.innerHTML.includes("data-break-from")
    })
  }

  setTimeField(fieldName, value) {
    if (!value) return

    const input = this.findFormInput(fieldName)
    if (!input) return

    input.value = value
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  findFormInput(fieldName) {
    const form = this.element.closest("form") || this.element.querySelector("form")
    if (!form) return null

    return (
      form.querySelector(`[name*="[${fieldName}]"]`) ||
      form.querySelector(`[name="${fieldName}"]`) ||
      form.querySelector(`#configurable_time_sheet_${fieldName}`)
    )
  }

  get form() {
    return this.element.closest("form") || this.element.querySelector("form")
  }

  // The (multi-)select of employees the entry is being created for, if any.
  employeeSelect() {
    const form = this.form
    return form ? form.querySelector('select[name*="[employee_ids]"]') : null
  }

  // The single selected employee id, or null when zero/multiple are selected.
  selectedEmployeeId() {
    const select = this.employeeSelect()
    if (!select) return null

    const ids = Array.from(select.selectedOptions)
      .map((option) => option.value)
      .filter(Boolean)
    return ids.length === 1 ? ids[0] : null
  }
}
